import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ErrorBar from 'components/ErrorBar';
import { MOBILE_VIEW } from 'libs/windowDimension';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import LabelledInput from 'components/LabelledInput';
import { changeSubscriptionActivateFields, clickSubscriptionActivatePaymentDetailsButton } from 'libs/cleverTap';
import Loader from 'components/Loader';
import { CardComponent, CardNumber, CardExpiry, CardCVV } from '@chargebee/chargebee-js-react-wrapper';
import Button from 'widgets/Button';
import {
  PAYMENT_INFO_KEYS, ERROR_INFO_KEYS,
  PAYMENT_ERROR_CONFIG,
  checkErrors
} from './config';
import styles from './style';
import SectionHeader from '../SectionHeader';
import './Payment.scss';
import { formatMessage } from 'libs/notice';

class Payment extends React.PureComponent {
  constructor(props) {
    super(props);
    this.CardRef = React.createRef();
    this.state = {
      paymentInfo: {
        [PAYMENT_INFO_KEYS.FULL_NAME]: '',
      },
      classes: {
        focus: 'SubscriptionActivate__Payment__ContentWrap__Content__Card__Input__Focus',
        invalid: 'SubscriptionActivate__Payment__ContentWrap__Content__Card__Input__Invalid'
      },
      errorInfo: {
        [ERROR_INFO_KEYS.PAYMENT_FORM]: {},
        errorConfigPayment: PAYMENT_ERROR_CONFIG
      }
    };
    this.isPaymentFormDirty = false;
  }

  componentDidMount = () => {
    const { auth: { info: userInfo } } = this.props;

    if (userInfo) {
      this.handleGetActivationCodeCurrency();
    }
  }

  handleGetActivationCodeCurrency = () => {
    const { subscriptionActivate: { activationCode } } = this.props;

    this.props.getActivationCodeCurrency(
      activationCode,
      this.handleGetActivationCodeCurrencyCallback
    );
  }

  handleGetActivationCodeCurrencyCallback = ({
    message, isSuccess
  }) => {
    if (message) {
      this.props.showErrorBar({ message });
    }

    if (isSuccess) {
      this.props.closeErrorBar();
    }
  }

  handleChangeCard = (e) => {
    const errorInfo = { ...this.state.errorInfo };
    const errorData = errorInfo[ERROR_INFO_KEYS.PAYMENT_FORM];
    const { error } = e;
    if (error) {
      errorData[e.field] = error.message;
    } else {
      delete errorData[e.field];
    }
    this.setState({
      errorInfo: {
        ...this.state.errorInfo,
        [ERROR_INFO_KEYS.PAYMENT_FORM]: errorData,
      },
    });
    changeSubscriptionActivateFields(e.field); // track event
  }

  handleChange = (e, cb) => {
    const paymentInfo = { ...this.state.paymentInfo };
    paymentInfo[e.target.name] = e.target.value;
    this.setState({ paymentInfo }, cb);
    changeSubscriptionActivateFields(e.target.name); // track event
  }

  handlePaymentFormInputChange = e => this.handleChange(e, () => {
    if (this.isPaymentFormDirty) {
      const { errorConfigPayment } = this.state.errorInfo;
      this.validateFields(errorConfigPayment);
    }
  });

  validateFields = (config = {}) => {
    this.props.closeErrorBar();
    const paymentInfo = { ...this.state.paymentInfo };
    const errorInfo = { ...this.state.errorInfo };
    const { errorFields } = config;

    const errors = checkErrors(errorFields, paymentInfo);
    errorInfo[config.errorKey] = errors;
    this.setState({ errorInfo });

    return errorInfo;
  }

  handlePaymentFormSave = () => {
    const cardPromise = this.CardRef.current.tokenize();
    const { errorConfigPayment } = this.state.errorInfo;
    const errors = this.validateFields(errorConfigPayment);

    this.isPaymentFormDirty = true;

    if (Object.keys(errors[ERROR_INFO_KEYS.PAYMENT_FORM]).length) {
      return;
    }

    cardPromise.then((data) => {
      const token = data.token;
      this.checkoutPayment(token);
    }).catch((error) => {
      this.props.showErrorBar({
        message: formatMessage('views.subscriptionActivate.payment.contentWrap.content.card.tokenize.error')
      });
    });
  };

  checkoutPayment = (token) => {
    this.props.checkoutPayment(
      token, this.checkoutPaymentCallback
    );
    clickSubscriptionActivatePaymentDetailsButton(); // track event
  }

  checkoutPaymentCallback = ({
    message, isSuccess
  }) => {
    if (message) {
      this.props.showErrorBar({ message });
      this.setState({
        paymentInfo: {
          ...this.state.paymentInfo,
          [PAYMENT_INFO_KEYS.FULL_NAME]: '',
        },
      });
    }

    if (isSuccess) {
      this.props.checkoutSuccess();
    }
  }

  render() {
    const {
      currentSection, setCurrentSection,
      sectionHeader: { step, label, actionLabel },
      payment: { info, isFetching }, isActive,
      subscriptionActivate: { activationCodeCurrency },
      windowDimension,
      i18n: { info: { locale } }, intl
    } = this.props;

    const {
      errorInfo,
      paymentInfo,
      classes
    } = this.state;
    const errorInfoForm = errorInfo[ERROR_INFO_KEYS.PAYMENT_FORM] || {};
    const showCreditCardIcons = MOBILE_VIEW(windowDimension);

    return (
      <div className="SubscriptionActivate__Payment">
        <div className="SubscriptionActivate__Payment__Content__Divider" />
        <SectionHeader
          step={step}
          label={label}
          isEdit={!!info}
          handleEdit={setCurrentSection}
          activeSection={currentSection}
          actionLabel={actionLabel}
          windowDimension={windowDimension}
          showCreditCardIcons={showCreditCardIcons}
        />
        {
          isFetching ?
            <div className="SubscriptionActivate__Payment__Loader"><Loader /></div> :
          isActive &&
            <div className="SubscriptionActivate__Payment__ContentWrap">
              <div className="SubscriptionActivate__Payment__ContentWrap__Content__Details">
                <FormattedHTMLMessage id="views.subscriptionActivate.payment.contentWrap.contentDetails" />
              </div>
              <div className="SubscriptionActivate__Payment__ContentWrap__Content">
                <div className="SubscriptionActivate__Payment__ContentWrap__Content__Title">
                  <span><FormattedMessage id="views.subscriptionActivate.payment.contentWrap.content.title" /></span>
                  <div className="SubscriptionActivate__Payment__ContentWrap__Content__CreditCard__Container">
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__CreditCard__Item">
                      <img src="/images/credit-cards_mastercard.png" alt="img" width={30} height={20} />
                    </div>
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__CreditCard__Item">
                      <img src="/images/credit-cards_visa.png" alt="img" width={30} height={20} />
                    </div>
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__CreditCard__Item">
                      <img src="/images/credit-cards_amex.png" alt="img" width={30} height={20} />
                    </div>
                  </div>
                </div>
                <div className="SubscriptionActivate__Payment__ContentWrap__Content__Text">
                  <span>
                    <FormattedMessage id="views.subscriptionActivate.payment.contentWrap.content.card.description" />
                  </span>
                </div>
                {/* Payment component */}
                <form id="SubscriptionActivate__Payment">
                  <CardComponent
                    ref={this.CardRef}
                    styles={styles.PaymentCardStyle}
                    className="SubscriptionActivate__Payment__ContentWrap__Content__Card"
                    onChange={this.handleChangeCard}
                    locale={locale}
                    currency={activationCodeCurrency}
                    classes={classes}
                  >
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputWrap">
                      <CardNumber
                        className={`SubscriptionActivate__Payment__ContentWrap__Content__Card__Input ${errorInfoForm[PAYMENT_INFO_KEYS.CARD_NUMBER] ? 'SubscriptionActivate__Payment__Input__Error' : ''}`}
                        placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.payment.contentWrap.content.card.input.cardNumber.placeholder' })}
                      />
                      {
                        !!errorInfoForm[PAYMENT_INFO_KEYS.CARD_NUMBER] &&
                        <span className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputError">
                          {errorInfoForm[PAYMENT_INFO_KEYS.CARD_NUMBER]}
                        </span>
                      }
                    </div>
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputWrap">
                      <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__Input__Name">
                        <LabelledInput
                          name={PAYMENT_INFO_KEYS.FULL_NAME}
                          onChange={this.handlePaymentFormInputChange}
                          placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.payment.contentWrap.content.card.input.fullName.placeholder' })}
                          value={paymentInfo[PAYMENT_INFO_KEYS.FULL_NAME]}
                          errorText={errorInfoForm[PAYMENT_INFO_KEYS.FULL_NAME] &&
                            intl.formatMessage({ id: errorInfoForm[PAYMENT_INFO_KEYS.FULL_NAME] })}
                          onEnter={this.handleChange}
                        />
                      </div>
                    </div>
                    <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputWrap__Container">
                      <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputWrap">
                        <CardExpiry
                          className={`SubscriptionActivate__Payment__ContentWrap__Content__Card__Input ${errorInfoForm[PAYMENT_INFO_KEYS.EXPIRY_DATE] && 'SubscriptionActivate__Payment__Input__Error'}`}
                          placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.payment.contentWrap.content.card.input.cardExpiry.placeholder' })}
                        />
                        {
                          !!errorInfoForm[PAYMENT_INFO_KEYS.EXPIRY_DATE] &&
                          <span className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputError">
                            {errorInfoForm[PAYMENT_INFO_KEYS.EXPIRY_DATE]}
                          </span>
                        }
                      </div>
                      <div className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputWrap">
                        <CardCVV
                          className={`SubscriptionActivate__Payment__ContentWrap__Content__Card__Input ${errorInfoForm[PAYMENT_INFO_KEYS.CVV] && 'SubscriptionActivate__Payment__Input__Error'}`}
                          placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.payment.contentWrap.content.card.input.cardCVV.placeholder' })}
                        />
                        {
                          !!errorInfoForm[PAYMENT_INFO_KEYS.CVV] &&
                          <span className="SubscriptionActivate__Payment__ContentWrap__Content__Card__InputError">
                            {errorInfoForm[PAYMENT_INFO_KEYS.CVV]}
                          </span>
                        }
                      </div>
                    </div>
                  </CardComponent>
                </form>
              </div>
              <div className="SubscriptionActivate__Payment__ContentWrap__Action">
                <Button
                  id="SubscriptionActivate__Payment__Action"
                  cssStyles={{
                    root: styles.PaymentButtonStyle,
                    label: styles.PaymentButtonLabelStyle,
                  }}
                  className={`SubscriptionActivate__Payment__ContentWrap__ActionButton ${(isFetching && 'Disable') || ''}`}
                  disabled={isFetching}
                  onClick={this.handlePaymentFormSave}
                >
                  <FormattedMessage id="views.subscriptionActivate.payment.contentWrap.actionButton.buttonLabel" />
                </Button>
              </div>
            </div>
        }
      </div>
    );
  }
}

Payment.propTypes = {
  isActive: PropTypes.bool.isRequired,
  currentSection: PropTypes.string.isRequired,
  setCurrentSection: PropTypes.func.isRequired,
  sectionHeader: PropTypes.object.isRequired,
  payment: PropTypes.object.isRequired,
  checkoutPayment: PropTypes.func.isRequired,
  windowDimension: PropTypes.object.isRequired,
  subscriptionActivate: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
  checkoutSuccess: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired
};

function mapStateToProps({
  i18n
}) {
  return {
    i18n
  };
}

export default connect(
  mapStateToProps
)(injectIntl(Payment));



// WEBPACK FOOTER //
// ./src/views/SubscriptionActivate/Payment/Payment.jsx